import React from 'react';
import { useAnalytics } from './AdvancedAnalyticsProvider';
import { X, TrendingUp, Users, DollarSign, Calendar, MapPin, Eye, EyeOff } from 'lucide-react';
import { InteractiveChart } from './InteractiveChart';
import { ChartDataPoint } from '../../types';

interface DrillDownPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DrillDownPanel: React.FC<DrillDownPanelProps> = ({ isOpen, onClose }) => {
  const { drillDownData, chartData, filters, setDrillDownData } = useAnalytics();

  if (!isOpen || !drillDownData) {
    return null;
  }

  const renderContent = () => {
    switch (drillDownData.type) {
      case 'timePoint':
        return <TimePointDrillDown data={drillDownData.data} index={drillDownData.index} />;
      
      case 'activityType':
        return <ActivityTypeDrillDown label={drillDownData.label} value={drillDownData.value} />;
      
      case 'memberDetails':
        return <MemberDetailsDrillDown memberId={drillDownData.memberId} />;
      
      case 'contributionDetails':
        return <ContributionDetailsDrillDown contributionId={drillDownData.contributionId} />;
      
      default:
        return <DefaultDrillDown data={drillDownData} />;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-dark-bg-secondary text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="rounded-md bg-white dark:bg-dark-bg-secondary text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <span className="sr-only">Close</span>
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white dark:bg-dark-bg-secondary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                    Detailed Analysis
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setDrillDownData(null)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <EyeOff className="h-4 w-4" />
                      Clear Selection
                    </button>
                  </div>
                </div>
                
                <div className="mt-4">
                  {renderContent()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Time Point Drill Down Component
const TimePointDrillDown: React.FC<{ data: ChartDataPoint; index: number }> = ({ data, index }) => {
  const { chartData } = useAnalytics();

  // Get trend data around this point
  const trendData = chartData.slice(Math.max(0, index - 6), Math.min(chartData.length, index + 7));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400">Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">GH₵{data.revenue.toLocaleString()}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 dark:text-green-400">Members</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{data.members.toLocaleString()}</p>
            </div>
            <Users className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 dark:text-purple-400">Contributions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{data.contributions}</p>
            </div>
            <DollarSign className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 dark:text-orange-400">Activities</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{data.activities}</p>
            </div>
            <Calendar className="h-8 w-8 text-orange-600 dark:text-orange-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Trend Analysis</h4>
          <InteractiveChart
            type="line"
            title="Performance Trend"
            height={250}
            data={{
              labels: trendData.map(d => d.time),
              datasets: [
                {
                  label: 'Revenue',
                  data: trendData.map(d => d.revenue),
                  borderColor: '#3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  tension: 0.4,
                  fill: true
                }
              ]
            }}
          />
        </div>

        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Activity Breakdown</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Contributions</span>
              <span className="font-medium">{data.contributions}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Benefits</span>
              <span className="font-medium">{data.benefits}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Events</span>
              <span className="font-medium">{data.events}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Expenses</span>
              <span className="font-medium">{data.expenses}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Activity Type Drill Down Component
const ActivityTypeDrillDown: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary">{label} Analysis</h4>
          <span className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary">{value}</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-600 dark:text-blue-400">Total Count</p>
            <p className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">{value}</p>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-sm text-green-600 dark:text-green-400">Average per Month</p>
            <p className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">{Math.round(value / 12)}</p>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <p className="text-sm text-purple-600 dark:text-purple-400">Growth Rate</p>
            <p className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">+12%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Member Details Drill Down Component
const MemberDetailsDrillDown: React.FC<{ memberId: string }> = ({ memberId }) => {
  // This would fetch member details from API
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Member Details</h4>
        <p className="text-gray-600 dark:text-gray-300">Loading member details for ID: {memberId}</p>
      </div>
    </div>
  );
};

// Contribution Details Drill Down Component
const ContributionDetailsDrillDown: React.FC<{ contributionId: string }> = ({ contributionId }) => {
  // This would fetch contribution details from API
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Contribution Details</h4>
        <p className="text-gray-600 dark:text-gray-300">Loading contribution details for ID: {contributionId}</p>
      </div>
    </div>
  );
};

// Default Drill Down Component
const DefaultDrillDown: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6">
      <h4 className="text-md font-semibold text-gray-900 dark:text-dark-text-primary mb-4">Drill-down Data</h4>
      <pre className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg overflow-auto max-h-64">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};